import { useState } from 'react';

/**
 * Gets and sets browser storage values with React state.
 * @param storage the storage mechanism to use
 * @param key the key of the storage item
 * @param initialValue the initial value, or function to determine the initial value
 */
function useBrowserStorage<T>(
    storage: Storage,
    key: string,
    initialValue?: T | (() => T),
): [T, (value: T) => void, () => void] {
    function getValue(): T {
        const item = storage.getItem(key);
        const val = item ? JSON.parse(item) :
         initialValue;
        return val instanceof Function ? val() :
            val;
    }


const [storedValue, setStoredValue] =
    useState<T>(function () {
        try {
            return getValue();
        } catch (error) {
            console.error(error);
            return initialValue instanceof Function
            ? initialValue() : (initialValue as T
            );
        }
    });

function setValue(value: T) {
    try {
        const valueToStore = value instanceof
            Function ? value(storedValue) : value;
        storage.setItem(key, JSON.stringify(valueToStore));
        setStoredValue(valueToStore);
    } catch (error) {
        console.error(error);
    }
}

function removeValue() {
    try {
        storage.removeItem(key);
        setStoredValue(undefined as T);
    } catch (error) {
        console.error(error);
    }
}

    return [storedValue, setValue, removeValue];
}

/**
 * Gets and sets browser storage values without any state updates.
 * @param storage the storage mechanism to use
 * @param key the key of storage item
 * @param initialValue the initial value, or function to determine the initial value
 */
function useStatelessBrowserStorage<T>(
    storage: Storage,
    key: string,
    initialValue?: T | (() => T),
): [T, (value: T) => void, () => void] {
    function getValue(): T{
        const item = storage.getItem(key);
        const val = item ? JSON.parse(item) :
        initialValue;
        return val instanceof Function ? val() :
            val;
    }

const storedValue = getValue();

function setValue(value: T) {
    try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        storage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
        console.error(error);
    }
}

function removeValue() {
    try {
        storage.removeItem(key);
    } catch (error) {
        console.error(error);
    }
}

return [storedValue, setValue, removeValue];

}

export function createBrowserStorageHook(storage: Storage) {
    return <T>(key: string, initialValue?: T | (() => T)) =>
    useBrowserStorage<T>(storage, key, initialValue);
}

export function createStatelessBrowserStorageHook(storage: Storage) {
    return <T>(key: string, initialValue?: T | (() => T)) =>
      useStatelessBrowserStorage<T>(storage, key, initialValue);
  }
  
